import { AlertTriangle } from 'lucide-react';

import { cn } from '../lib/utils';
import { Button } from '../shadcn/button';

/**
 * Stufe 4 aus Blatt P1 — der Streifen, der stehen bleibt, bis jemand ihn behebt.
 *
 * 🔴 WARUM DAS KEIN TOAST SEIN DARF. P1: „Für Zustände, die jemand beheben muss.
 * Kein flüchtiger Hinweis, sondern ein Streifen an der Stelle, wo es zählt — und
 * zusätzlich in der Glocke." Ein Hinweis, der nach fünf Sekunden verschwindet,
 * kann eine abgelehnte Portal-Anmeldung nicht tragen: sie ist noch da, wenn er
 * weg ist.
 *
 * 🔴 DIE REIHENFOLGE IST TEIL DER REGEL: Zustand, dann Folge, dann Ausweg. Nicht
 * „Bitte neu verbinden (Kleinanzeigen abgelehnt)" — sondern „Kleinanzeigen hat
 * die Anmeldung abgelehnt. Zwei Inserate warten auf Übertragung." und dann der
 * Knopf. Wer die Folge weglässt, macht aus einer Dringlichkeit eine Randnotiz.
 *
 * Wer die Stufe 4 trägt: Besichtigungsphase beendet, Portal-Übertragung
 * fehlgeschlagen, Postfach nicht erreichbar, Abo läuft aus ohne Zahlungsmittel.
 *
 * 🔴 SAMMELABSAGE UND SAMMELUMBUCHUNG STEHEN HIER NICHT MEHR — und das war die
 * letzte Stelle im Baum, die sie noch auf Stufe 4 führte.
 *
 * Blatt P zählte sie ursprünglich hierher. Blatt `00`, Antwort 3 (29.08.2026)
 * hat die Zeile verschoben: „Eine fertige Sammelaktion ist kein Zustand,
 * sondern ein Ergebnis — und das Ergebnis ist die veränderte Liste. Verweis
 * dorthin, 5 s, kein Streifen." Also Stufe 2.
 *
 * ⚠️ „Besichtigungsphase beendet" ist davon AUSDRÜCKLICH NICHT MITGEWANDERT.
 * Blatt P führt sie als eigene Zeile neben den Sammelaktionen, und genau daran
 * hing die Frage, ob „Sammelaktionen" beide meint. Antwort 52 (Paket 20,
 * 31.08.2026) entscheidet sie: „Stufe 4 bleibt — «Sammelaktionen» meinte nur
 * Sammelabsage und -umbuchung. Eine beendete Phase ist ein Zustand, der
 * bleibt — genau das Merkmal, an dem P Stufe 4 festmacht."
 *
 * 🔴 Stufe 3 ist GESTRICHEN. Sie trug die Sammelaktionen, solange es die
 * Warteschlange gab; mit deren Wegfall (P6) sind sie nicht hierher gerückt,
 * sondern auf 2. Die Sicherung liegt in der Bestätigung VOR der Handlung, mit
 * Anzahl und Folge im Satz — nicht in einem Streifen danach.
 */
export function Systemstreifen({
  zustand,
  folge,
  ausweg,
  className,
}: {
  /** Was ist: „Kleinanzeigen hat die Anmeldung abgelehnt." */
  zustand: React.ReactNode;
  /** Was daraus folgt: „Zwei Inserate warten auf Übertragung." Pflicht. */
  folge: React.ReactNode;
  /** Der Weg heraus: „Neu verbinden". Pflicht — ein Zustand ohne Ausweg ist eine Sackgasse. */
  ausweg: { label: React.ReactNode; onClick?: () => void; href?: string };
  className?: string;
}) {
  return (
    <div
      data-test="systemstreifen"
      role="status"
      className={cn(
        'border-warning/40 bg-warning-subtle rounded-card flex flex-wrap items-center gap-3 border px-4 py-3',
        className,
      )}
    >
      <AlertTriangle className="text-warning size-5 shrink-0" aria-hidden />

      <p className="min-w-0 flex-1 text-sm">
        <span className="font-medium">{zustand}</span>{' '}
        <span className="text-muted-foreground">{folge}</span>
      </p>

      {ausweg.href ? (
        <Button
          asChild
          size="sm"
          variant="quiet"
          data-test="systemstreifen-ausweg"
        >
          <a href={ausweg.href}>{ausweg.label}</a>
        </Button>
      ) : (
        <Button
          size="sm"
          variant="quiet"
          onClick={ausweg.onClick}
          data-test="systemstreifen-ausweg"
        >
          {ausweg.label}
        </Button>
      )}
    </div>
  );
}
